import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from '../AuthContext';
import "./AdminEditProduct.css";
import Swal from 'sweetalert2';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminEditProduct() {
  const { id } = useParams();
  const { authFetch } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [hasExistingImage, setHasExistingImage] = useState(false);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [imageUpdates, setImageUpdates] = useState({
    updated: false,
    removeImageIds: [],
    setMainImageId: null
  });
  const MAX_ADDITIONAL_IMAGES = 4;

  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [stockQuantityModified, setStockQuantityModified] = useState(false);
  //const [batchStockQuantityModified, setBatchStockQuantityModified] = useState(false);
  const [specifications, setSpecifications] = useState([]);
  const [newSpecificationName, setNewSpecificationName] = useState("");
  const [specNameError, setSpecNameError] = useState(false);
  const [specValueErrors, setSpecValueErrors] = useState([]);
  const [saveError, setSaveError] = useState("");
  const [activeTab, setActiveTab] = useState("details");
  const [isIncreaseByMOQ, setIsIncreaseByMOQ] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const [product, setProduct] = useState({
    name: "",
    description: "",
    price: null,
    categoryID: "",
    stockQuantity: null,
    batchStockQuantity: null,
    moq: null,
    productCode: "",
    mainImageUrl: null,
    additionalImages: [],
    specifications: [],
    variants: []
  });

  const validateStockQuantities = () => {
    if (product.stockQuantity && product.stockQuantity < 0) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Stock Quantity',
        text: 'Stock quantity cannot be negative.',
        confirmButtonColor: '#e74c3c'
      });
      return false;
    }

    // if (product.batchStockQuantity && product.batchStockQuantity < 0) {
    //   Swal.fire({
    //     icon: 'error',
    //     title: 'Invalid Batch Stock Quantity',
    //     text: 'Batch stock quantity cannot be negative.',
    //     confirmButtonColor: '#e74c3c'
    //   });
    //   return false;
    // }

    // if (product.stockQuantity && !product.batchStockQuantity) {
    //   Swal.fire({
    //     icon: 'error',
    //     title: 'Invalid Batch Stock Quantity',
    //     text: 'Batch stock quantity must be set if stock quantity is set.',
    //     confirmButtonColor: '#e74c3c'
    //   });
    //   return false;
    // }

    // MOQ is optional. Only validate it when a value was entered.
    if (product.moq !== "" && product.moq !== null && product.moq !== undefined && product.moq < 1) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid MOQ',
        text: 'MOQ must be more than 0.',
        confirmButtonColor: '#e74c3c'
      });
      return false;
    }

    return true;
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/products/${id}`);
        if (!response.ok) {
          throw new Error("Failed to fetch product");
        }
        const data = await response.json();
        setProduct(data);
        setCategory(data.categoryID);
        setIsIncreaseByMOQ(data.isIncreaseByMOQ ?? false);
        setIsActive(data.isActive ?? true); 

        const specs = (data.specifications || []).map((spec) => ({
          ...spec,
          values: spec.specificationValues.map((sv) => sv.value),
          specificationValueIds: spec.specificationValues.map((sv) => sv.specificationValueId),
        }));

        setSpecifications(specs);

        if (data.mainImageUrl) {
          setMainImagePreview(`${data.mainImageUrl}`);
          setHasExistingImage(true);
        }

        if (data.additionalImages && data.additionalImages.length > 0) {
          const additionalImagesData = data.additionalImages.map(img => ({
            id: img.imageId,
            preview: `${img.imageUrl}`,
            displayOrder: img.displayOrder,
            isExisting: true
          }));

          setAdditionalImages(additionalImagesData);
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/products/categories`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }
        const data = await response.json();
        const filteredCategories = data.filter(category => !category.hasSubcategories);
        setCategories(filteredCategories);
      } catch (error) {
        // Silent fail (previously only 1 console.error line in this catch block, no other actions)
      }
    };

    fetchProduct();
    fetchCategories();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let parsedValue = value;
    if (name === 'price' || name === 'stockQuantity' || name === 'moq') {
      parsedValue = value === '' ? null : (name === 'price' ? parseFloat(value) : parseInt(value));
    }

    if (name === 'stockQuantity') {
      setStockQuantityModified(true);

      setProduct(prev => {
        const newState = { ...prev, stockQuantity: parsedValue };

        // setTimeout(() => {
        //   const element = document.getElementById('stockQuantity');
        //   if (parsedValue && prev.batchStockQuantity && parsedValue > prev.batchStockQuantity) {
        //     element?.classList.add('admin-edit-product-input-warning');
        //   } else {
        //     element?.classList.remove('admin-edit-product-input-warning');
        //   }
        // }, 0);

        return newState;
      });
    } else if (name === 'isIncreaseByMOQ') { 
      setIsIncreaseByMOQ(e.target.checked);
    }
    // else if (name === 'batchStockQuantity') {
    //   setBatchStockQuantityModified(true);

    //   setProduct(prev => {
    //     const newState = { ...prev, batchStockQuantity: parsedValue };
    //     setTimeout(() => {
    //       const stockField = document.getElementById('stockQuantity');
    //       if (prev.stockQuantity && parsedValue && prev.stockQuantity > parsedValue) {
    //         stockField?.classList.add('admin-edit-product-input-warning');
    //       } else {
    //         stockField?.classList.remove('admin-edit-product-input-warning');
    //       }
    //     }, 0);
    //     return newState;
    //   });
    // }
    else {
      setProduct(prev => ({ ...prev, [name]: parsedValue }));
    }
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'Please select an image under 5MB.',
          confirmButtonColor: '#e74c3c'
        });
        return;
      }
      const extension = file.name.split('.').pop().toLowerCase();
      const allowedExtensions = ['jpg', 'jpeg', 'png'];
      if (!allowedExtensions.includes(extension)) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File Type',
          text: 'Please select a valid image file (JPG, JPEG, PNG only).',
          confirmButtonColor: '#e74c3c'
        });
        return;
      }

      setMainImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMainImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearMainImage = () => {
    Swal.fire({
      title: 'Remove Main Image?',
      text: 'Are you sure you want to remove this image?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Yes'
    }).then((result) => {
      if (result.isConfirmed) {
        setMainImageFile(null);
        setMainImagePreview(null);
        setHasExistingImage(false);
        setImageUpdates(prev => ({ ...prev, updated: true }));
      }
    });
  };

  const handleAdditionalImageChange = (e) => {
    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    if (additionalImages.length + files.length > MAX_ADDITIONAL_IMAGES) {
      Swal.fire({
        icon: 'warning',
        title: 'Too Many Images',
        text: `You can upload a maximum of ${MAX_ADDITIONAL_IMAGES} additional images.`,
        confirmButtonColor: '#e74c3c'
      });
      return;
    }

    // Validate files
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'Please select images under 5MB.',
          confirmButtonColor: '#e74c3c'
        });
        return;
      }

      const extension = file.name.split('.').pop().toLowerCase();
      const allowedExtensions = ['jpg', 'jpeg', 'png'];
      if (!allowedExtensions.includes(extension)) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File Type',
          text: 'Please select valid image files (JPG, JPEG, PNG only).',
          confirmButtonColor: '#e74c3c'
        });
        return;
      }
    }

    const newImages = [];

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newImages.push({
          file,
          preview: reader.result,
          displayOrder: additionalImages.length + newImages.length + 1,
          isNew: true
        });

        if (newImages.length === files.length) {
          const combinedImages = [...additionalImages, ...newImages];

          combinedImages.forEach((img, i) => {
            img.displayOrder = i + 1;
          });

          setAdditionalImages(combinedImages);
          setImageUpdates(prev => ({ ...prev, updated: true }));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveAdditionalImage = (index) => {
    Swal.fire({
      title: 'Remove Image?',
      text: 'Are you sure you want to remove this image?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Yes'
    }).then((result) => {
      if (result.isConfirmed) {
        const imageToRemove = additionalImages[index];

        if (imageToRemove.id && !imageToRemove.isNew) {
          setImageUpdates(prev => ({
            ...prev,
            updated: true,
            removeImageIds: [...prev.removeImageIds, imageToRemove.id]
          }));
        }

        const newImages = [...additionalImages];
        newImages.splice(index, 1);

        newImages.forEach((img, i) => {
          img.displayOrder = i + 1;
        });

        setAdditionalImages(newImages);
        setImageUpdates(prev => ({ ...prev, updated: true }));
      }
    });
  };

  const moveAdditionalImage = (index, direction) => {
    if ((direction === 'up' && index === 0) ||
      (direction === 'down' && index === additionalImages.length - 1)) {
      return;
    }

    const newImages = [...additionalImages];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    [newImages[index], newImages[targetIndex]] = [newImages[targetIndex], newImages[index]];

    newImages.forEach((img, i) => {
      img.displayOrder = i + 1;
    });

    setAdditionalImages(newImages);
    setImageUpdates(prev => ({ ...prev, updated: true }));
  };

  const handleSetAsMainImage = (index) => {
    Swal.fire({
      title: 'Set as Main Image?',
      text: 'Are you sure you want to set this as the main product image?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Yes'
    }).then((result) => {
      if (result.isConfirmed) {
        const imageToSetAsMain = additionalImages[index];

        const oldMainImagePreview = mainImagePreview;

        setMainImagePreview(imageToSetAsMain.preview);
        setMainImageFile(imageToSetAsMain.file || null);
        setHasExistingImage(true);

        if (imageToSetAsMain.id && !imageToSetAsMain.isNew) {
          setImageUpdates(prev => ({
            ...prev,
            updated: true,
            setMainImageId: imageToSetAsMain.id
          }));
        }

        const newImages = [...additionalImages];
        newImages.splice(index, 1);

        if (oldMainImagePreview && hasExistingImage) {
          newImages.push({
            preview: oldMainImagePreview,
            displayOrder: newImages.length + 1,
            isFromMain: true
          });
        }

        newImages.forEach((img, i) => {
          img.displayOrder = i + 1;
        });

        setAdditionalImages(newImages);
        setImageUpdates(prev => ({ ...prev, updated: true }));
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError("");

    if (!mainImageFile && !hasExistingImage) {
      Swal.fire({
        icon: 'error',
        title: 'Image Required',
        text: 'A product must have an image. Please upload an image.',
        confirmButtonColor: '#e74c3c'
      });
      return;
    }

    if (!validateStockQuantities()) {
      return;
    }

    if (product.price && product.price <= 0) {
      Swal.fire({
        icon: 'error',
        title: 'Price Required',
        text: 'A price greater than zero is required.',
        confirmButtonColor: '#e74c3c'
      });
      return;
    }

    if (activeTab === "specifications") {
      if (specifications.length > 0) {
        const hasEmptySpec = specifications.some(
          (spec) => spec.values.length === 0
        );
        if (hasEmptySpec) {
          setSaveError("Each specification must have at least one value");
          return;
        }

        const hasEmptyValue = specifications.some((spec) =>
          spec.values.some((value) => !value.trim())
        );
        if (hasEmptyValue) {
          setSaveError("All specification values must be filled");
          return;
        }
      }
    }
    Swal.fire({
      title: 'Saving Changes',
      text: 'Please wait...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const formData = new FormData();
      formData.append("Name", product.name);
      formData.append("Description", product.description);
      if (product.price !== null && product.price !== undefined) {
        formData.append("Price", product.price);
      }
      formData.append("CategoryID", category);
      if (product.stockQuantity !== null && product.stockQuantity !== undefined) {
        formData.append("StockQuantity", product.stockQuantity);
      }
      // if (product.batchStockQuantity !== null && product.batchStockQuantity !== undefined) {
      //   formData.append("BatchStockQuantity", product.batchStockQuantity);
      // }
      // MOQ and UOM are optional - send "" (not null) so blank binds to null server-side
      formData.append("moq", product.moq ?? "");
      formData.append("ProductCode", product.productCode);
      formData.append("IsRedeemable", false);
      formData.append("UOM", product.uom ?? "");
      formData.append("IsIncreaseByMOQ", isIncreaseByMOQ);
      formData.append("IsActive", isActive);
      if (imageUpdates.updated) {
        formData.append("UpdateImages", "true");

        if (mainImageFile) {
          formData.append("ImageFile", mainImageFile);
        }

        const newAdditionalImages = additionalImages
          .filter(img => img.isNew && img.file)
          .sort((a, b) => a.displayOrder - b.displayOrder);

        newAdditionalImages.forEach(img => {
          formData.append("AdditionalImageFiles", img.file);
        });

        newAdditionalImages.forEach((img, index) => {
          formData.append(`NewImageDisplayOrders[${index}]`, img.displayOrder);
        });

        if (imageUpdates.removeImageIds.length > 0) {
          imageUpdates.removeImageIds.forEach((id, index) => {
            formData.append(`RemoveImageIds[${index}]`, id);
          });
        }

        const existingImages = additionalImages.filter(img => img.id && !img.isNew);
        existingImages.forEach((img, index) => {
          formData.append(`ImageDisplayOrders[${index}].ImageId`, img.id);
          formData.append(`ImageDisplayOrders[${index}].DisplayOrder`, img.displayOrder);
        });

        if (imageUpdates.setMainImageId) {
          formData.append("SetMainImageId", imageUpdates.setMainImageId);
        }
      }
      else if (mainImageFile) {
        formData.append("ImageFile", mainImageFile);
      }

      if (activeTab === "specifications") {
        formData.append("UpdateSpecifications", "true");
        specifications.forEach((spec, index) => {
          if (spec.specificationId) {
            formData.append(
              `Specifications[${index}].SpecificationId`,
              spec.specificationId
            );
          }
          formData.append(`Specifications[${index}].Name`, spec.name);
          spec.values.forEach((value, valueIndex) => {
            // formData.append(
            //   `Specifications[${index}].Value[${valueIndex}]`,
            //   value
            // );
            formData.append(`Specifications[${index}].ValuesWithIds[${valueIndex}].Value`, value);
            if (spec.specificationValueIds && spec.specificationValueIds[valueIndex]) {
              formData.append(
                `Specifications[${index}].ValuesWithIds[${valueIndex}].SpecificationValueId`,
                spec.specificationValueIds[valueIndex]
              );
            }
          });
        });
      } else {
        formData.append("UpdateSpecifications", "false");
      }

      const response = await authFetch(`${API_BASE_URL}/products/${id}`, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || errorData?.title || `Failed to update product (${response.status})`);
      }

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Product updated successfully',
        confirmButtonColor: '#45a049'
      }).then(() => {
        window.location.reload();
      });
    } catch (error) {
      setError(error);

      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: 'Failed to update product: ' + error.message,
        confirmButtonColor: '#e74c3c'
      });
    }
  };

  const handleBack = () => {
    navigate("/admin/products");
  };

  const handleRemoveProduct = async () => {

    const result = await Swal.fire({
      title: 'Remove Product',
      text: 'Are you sure you want to remove this product?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {

        Swal.fire({
          title: 'Removing Product',
          text: 'Please wait...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        const formData = new FormData();
        formData.append("Name", product.name);
        formData.append("Description", product.description);
        if (product.price !== null && product.price !== undefined) {
          formData.append("Price", product.price);
        }
        formData.append("CategoryID", category);
        if (product.stockQuantity !== null && product.stockQuantity !== undefined) {
          formData.append("StockQuantity", product.stockQuantity);
        }
        // if (product.batchStockQuantity !== null && product.batchStockQuantity !== undefined) {
        //   formData.append("BatchStockQuantity", product.batchStockQuantity);
        // }
        // MOQ and UOM are optional - send "" (not null) so blank binds to null server-side
        formData.append("moq", product.moq ?? "");
        formData.append("ProductCode", product.productCode);
        formData.append("UOM", product.uom ?? "");
        // Preserve the visibility flag - the DTO defaults to true, so omitting it here
        // would silently re-activate a hidden product.
        formData.append("IsActive", isActive);

        if (mainImageFile) {
          formData.append("ImageFile", mainImageFile);
        }
        formData.append("deletedAt", new Date().toISOString());
        formData.append("updateSpecifications", "false");
        formData.append("updateVariants", "false");

        const response = await authFetch(
          `${API_BASE_URL}/products/${id}`,
          {
            method: "PUT",
            body: formData,
          }
        );

        if (response.ok) {
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'Product removed successfully',
            timer: 1500,
            confirmButtonColor: '#45a049'
          }).then(() => {
            navigate("/admin/products");
          });
        } else {
          throw new Error("Failed to remove product");
        }
      } catch (error) {
        setError(error);
        Swal.fire({
          icon: 'error',
          title: 'Removal Failed',
          text: 'Failed to remove product: ' + error.message,
          confirmButtonColor: '#e74c3c'
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="admin-edit-product-loading">
        <div className="admin-edit-product-loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
        </div>
        <p>Loading product details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-edit-product-container">
        <div className="admin-edit-product-error">
          <i className="fas fa-exclamation-circle"></i> Error: {error.message}
        </div>
        <button className="admin-edit-product-back-btn" onClick={handleBack}>
          <i className="fas fa-arrow-left"></i> Back to Products
        </button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="admin-edit-product-container">
        <div className="admin-edit-product-error">
          <i className="fas fa-exclamation-triangle"></i> Product not found
        </div>
        <button className="admin-edit-product-back-btn" onClick={handleBack}>
          <i className="fas fa-arrow-left"></i> Back to Products
        </button>
      </div>
    );
  }

  return (
    <div className="admin-edit-product-container">
      {/* Back to Product */}
      <div className="admin-edit-product-header">
        <button className="admin-edit-product-back-btn" onClick={handleBack}>
          <i className="fas fa-arrow-left"></i> Back to Products
        </button>
      </div>
      <div className="admin-edit-product-title">
        <h1>Edit Product Details</h1>
      </div>

      {/* Tab Navigation */}
      <div className="admin-edit-product-tabs">
        <button
          className={`admin-edit-product-tab ${activeTab === "details" ? "active" : ""
            }`}
          onClick={() => setActiveTab("details")}
        >
          Product Details
        </button>
        <button
          className={`admin-edit-product-tab ${activeTab === "specifications" ? "active" : ""
            }`}
          onClick={() => setActiveTab("specifications")}
        >
          Specifications
        </button>
      </div>

      {/* Conditional rendering of forms based on active tab */}
      {activeTab === "details" && (
        <form onSubmit={handleSubmit} className="admin-edit-product-details-form">
          <div className="admin-edit-product-form-group">
            <label>Name:</label>
            <input
              type="text"
              name="name"
              value={product.name || ""}
              onChange={handleChange}
              required
            />
          </div>
          <div className="admin-edit-product-form-group">
            <label>Category:</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat.categoryID} value={cat.categoryID}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-edit-product-form-group">
            <label>Product Code:</label>
            <input
              type="text"
              name="productCode"
              value={product.productCode ?? ""}
              onChange={handleChange}
              required
            />
          </div>
          <div className="admin-edit-product-form-group">
            <label>Product UOM:</label>
            <input
              type="text"
              name="uom"
              value={product.uom ?? ""}
              onChange={handleChange}
            />
          </div>
          <div className="admin-edit-product-form-group">
            <label>Description:</label>
            <textarea
              type="text"
              name="description"
              value={product.description || ""}
              onChange={handleChange}
              required
            />
          </div>
          {/* <div className="admin-edit-product-form-group">
            <label>Batch Stock Quantity:</label>
            <input
              id="batchStockQuantity"
              type="number"
              name="batchStockQuantity"
              value={product.batchStockQuantity ?? ""}
              onChange={handleChange}
              placeholder="Batch stock quantity (optional for pre-order)"
            />
            <small className="admin-edit-product-help-text">
              When updated, current stock will reset to this value if not modified
            </small>
          </div> */}

          {/* Stock is always tracked on the PRODUCT (in base units). Variants consume
              this pool via their Bundle Qty, so this field applies to every product. */}
          <div className="admin-edit-product-form-group">
              <label>
                Current Stock Quantity:
                {product.variants && product.variants.length > 0 && (
                  <span style={{ fontWeight: 400, color: "#0288d1", marginLeft: "8px", fontSize: "13px" }}>
                    (total stock shared by all variants)
                  </span>
                )}
              </label>
              <input
                id="stockQuantity"
                type="number"
                name="stockQuantity"
                value={product.stockQuantity ?? ""}
                onChange={handleChange}
                placeholder="Current stock quantity (optional for pre-order)"
              />
              {product.variants && product.variants.length > 0 && (
                <small style={{ display: "block", marginTop: "6px", color: "#777" }}>
                  Each variant deducts its <strong>Bundle Qty</strong> from this total when ordered.
                </small>
              )}
              {/* {product.stockQuantity > product.batchStockQuantity && (
                <small className="admin-edit-product-warning-text">
                  Warning: Stock quantity exceeds batch stock quantity
                </small>
              )} */}
          </div>

          <div className="admin-edit-product-form-group">
            <label>Price (RM):</label>
            <input
              type="number"
              name="price"
              value={product.price ?? ""}
              onChange={handleChange}
              placeholder="Price (optional for pre-order)"
              step="0.01"
            />
          </div>
          <div className="admin-edit-product-form-group">
            <label>MOQ:</label>
            <input
              type="number"
              name="moq"
              value={product.moq ?? ""}
              onChange={handleChange}
            />
          </div>
          <div className="admin-edit-product-form-group"  style={{ textAlign: 'left' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' ,whiteSpace: 'nowrap'}}>Increase By MOQ: <input
              type="checkbox"
              name="isIncreaseByMOQ"
              value={isIncreaseByMOQ}
              checked={isIncreaseByMOQ}
              onChange={handleChange}
            /></label>

          </div>

          {/* Storefront visibility */}
          <div className="admin-edit-product-form-group" style={{ textAlign: 'left' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
              Active (show on website): <input
                type="checkbox"
                name="isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            </label>
            <small style={{ display: 'block', marginTop: '4px', color: '#777' }}>
              Uncheck to hide this product from customers. It stays here in the back office.
            </small>
          </div>

          {/* Product Images Section */}
          <div className="admin-edit-product-form-group">
            <label>Product Images:</label>
            <div className="admin-add-product-images-row">
              {/* Main Image Section */}
              <div className="admin-add-product-main-image-section">
                <div className="admin-add-product-main-image-label">Main Image</div>
                <div className="admin-add-product-image-item">
                  <label htmlFor="productMainImageUpload">
                    {mainImagePreview ? (
                      <div className="admin-add-product-image-card">
                        <img src={mainImagePreview} alt="Main Product Preview" />
                        <div className="admin-add-product-image-order-badge">1</div>
                      </div>
                    ) : (
                      <div className="admin-add-product-image-card">
                        <div className="admin-add-product-upload-placeholder">
                          <i className="fas fa-cloud-upload-alt"></i>
                          <div className="admin-add-product-upload-text">
                            <span>Main Image</span>
                            <span className="admin-add-product-upload-subtext">Max 5MB</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <input
                      id="productMainImageUpload"
                      type="file"
                      accept="image/*"
                      onChange={handleMainImageChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                  {mainImagePreview && (
                    <div className="admin-add-product-image-actions-visible">
                      <button
                        type="button"
                        className="admin-add-product-delete"
                        onClick={handleClearMainImage}
                        title="Remove image"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="admin-add-product-image-divider"></div>

              {/* Additional Images Section */}
              <div className="admin-add-product-additional-images-section">
                <div className="admin-add-product-additional-images-label">Additional Images (up to {MAX_ADDITIONAL_IMAGES})</div>
                <div className="admin-add-product-additional-images-container">
                  {/* Display additional images */}
                  {additionalImages.map((img, index) => (
                    <div key={index} className="admin-add-product-image-item">
                      <div className="admin-add-product-image-card">
                        <img src={img.preview} alt={`Additional ${index + 1}`} />
                        <div className="admin-add-product-image-order-badge">{index + 2}</div>
                      </div>
                      <div className="admin-add-product-image-actions-visible">
                        <button
                          type="button"
                          className="admin-add-product-edit"
                          onClick={() => moveAdditionalImage(index, 'up')}
                          disabled={index === 0}
                          title="Move up"
                        >
                          <i className="fas fa-arrow-up"></i>
                        </button>
                        <button
                          type="button"
                          className="admin-add-product-edit"
                          onClick={() => moveAdditionalImage(index, 'down')}
                          disabled={index === additionalImages.length - 1}
                          title="Move down"
                        >
                          <i className="fas fa-arrow-down"></i>
                        </button>
                        <button
                          type="button"
                          className="admin-add-product-edit"
                          onClick={() => handleSetAsMainImage(index)}
                          title="Set as main image"
                        >
                          <i className="fas fa-star"></i>
                        </button>
                        <button
                          type="button"
                          className="admin-add-product-delete"
                          onClick={() => handleRemoveAdditionalImage(index)}
                          title="Remove image"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Upload additional image button */}
                  {additionalImages.length < MAX_ADDITIONAL_IMAGES && (
                    <div className="admin-add-product-image-item">
                      <label htmlFor="additionalImageUpload">
                        <div className="admin-add-product-image-card">
                          <div className="admin-add-product-upload-placeholder">
                            <i className="fas fa-plus"></i>
                            <div className="admin-add-product-upload-text">
                              <span>Add Image</span>
                              <span className="admin-add-product-upload-subtext">Max 5MB</span>
                            </div>
                          </div>
                        </div>
                      </label>
                      <input
                        id="additionalImageUpload"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleAdditionalImageChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <button type="submit" className="admin-edit-product-btn-primary">
            <i className="fas fa-save"></i> Save Changes
          </button>
        </form>
      )}

      {/* Product Specification Section */}
      {activeTab === "specifications" && (
        <div className="admin-edit-product-specifications-form">
          {specifications.length === 0 ? (
            <div className="admin-edit-product-no-specs">
              <p>No specifications added yet. Add your first specification below.</p>
            </div>
          ) : (specifications.map((spec, index) => (
            <div key={index} className="admin-edit-product-specification-group">
              <label>{spec.name}:</label>
              {spec.values.map((value, valueIndex) => (
                <div
                  key={valueIndex}
                  className="admin-edit-product-specification-value-group"
                >
                  <label>Value {valueIndex + 1}:</label>
                  <input
                    type="text"
                    value={value}
                    className={
                      value.trim() === ""
                        ? "admin-edit-product-input-error"
                        : ""
                    }
                    onChange={(e) => {
                      const newSpecifications = specifications.map(
                        (s, specIndex) => {
                          if (specIndex === index) {
                            const newValues = [...s.values];
                            newValues[valueIndex] = e.target.value;
                            return { ...s, values: newValues };
                          }
                          return s;
                        }
                      );
                      setSpecifications(newSpecifications);
                    }}
                  />
                  <button
                    type="button"
                    className="admin-edit-product-btn-remove-value"
                    onClick={() => {
                      const newSpecifications = specifications.map(
                        (s, specIndex) => {
                          if (specIndex === index) {
                            const newValues = [...s.values];
                            newValues.splice(valueIndex, 1);
                            return { ...s, values: newValues };
                          }
                          return s;
                        }
                      );
                      setSpecifications(newSpecifications);
                    }}
                  >
                    <i className="fas fa-times"></i> Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="admin-edit-product-btn-add-value"
                disabled={spec.values.some((v) => v.trim() === "")}
                onClick={() => {
                  if (spec.values.some((v) => v.trim() === "")) {
                    // Update error state
                    const newSpecValueErrors = [...specValueErrors];
                    newSpecValueErrors[index] = true;
                    setSpecValueErrors(newSpecValueErrors);
                    return;
                  }

                  const newSpecValueErrors = [...specValueErrors];
                  newSpecValueErrors[index] = false;
                  setSpecValueErrors(newSpecValueErrors);

                  const newSpecifications = specifications.map(
                    (s, specIndex) => {
                      if (specIndex === index) {
                        return { ...s, values: [...s.values, ""] };
                      }
                      return s;
                    }
                  );
                  setSpecifications(newSpecifications);
                }}
              >
                <i className="fas fa-plus"></i> Add Value
              </button>

              {specValueErrors[index] && (
                <div className="admin-edit-product-error">
                  All specification values must be filled before adding a new
                  one
                </div>
              )}
              <button
                type="button"
                className="admin-edit-product-btn-remove-spec"
                onClick={() => {
                  const newSpecifications = [...specifications];
                  newSpecifications.splice(index, 1);
                  setSpecifications(newSpecifications);
                }}
              >
                <i className="fas fa-trash-alt"></i> Remove Specification
              </button>
            </div>
          ))
          )}
          <div className="admin-edit-product-add-spec-group">
            <label>New Specification Name:</label>
            <input
              type="text"
              value={newSpecificationName}
              onChange={(e) => setNewSpecificationName(e.target.value)}
              placeholder="Enter specification name"
            />
            <button
              type="button"
              className="admin-edit-product-btn-add-spec"
              disabled={!newSpecificationName.trim()}
              onClick={() => {
                if (!newSpecificationName.trim()) {
                  setSpecNameError(true);
                  return;
                }
                setSpecNameError(false);
                setSpecifications([
                  ...specifications,
                  { name: newSpecificationName, values: [""] },
                ]);
                setNewSpecificationName("");
              }}
            >
              <i className="fas fa-plus-circle"></i> Add Specification
            </button>
            {specNameError && (
              <div className="admin-edit-product-error">
                Specification name cannot be empty
              </div>
            )}
          </div>
          <button
            onClick={handleSubmit}
            className="admin-edit-product-btn-primary"
          >
            <i className="fas fa-save"></i> Save Specifications
          </button>
          {saveError && (
            <div className="admin-edit-product-error">{saveError}</div>
          )}
        </div>
      )}

      <div className="admin-edit-product-actions">
        <button
          type="button"
          className="admin-edit-product-btn-remove"
          onClick={handleRemoveProduct}
        >
          <i className="fas fa-trash-alt"></i> Remove Product
        </button>

        {specifications.length >= 1 && (
          <button
            className="admin-edit-product-btn-variant"
            onClick={() => navigate(`/admin/products/edit/${id}/variants`)}
          >
            <i className="fas fa-code-branch"></i> Edit Variants
          </button>
        )}
      </div>
    </div>
  );
}

export default AdminEditProduct;